import { isEmpty } from "lodash";
import { X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useWatch } from "react-hook-form";
import { SearchValue } from "../../../../components/ui";
import { Button } from "../../../../components/ui/common/button";
import { Card, CardContent } from "../../../../components/ui/common/card";
import { FormInput } from "../../../../components/ui/Input/FormInput/FormInput";
import { FormSelect } from "../../../../components/ui/Input/FormSelector/FormSelect";
import { FormTextarea } from "../../../../components/ui/Input/FormTextArea/FormTextarea";
import { ConfirmModal } from "../../../../components/ui/widget/ConfirmModal";
import { DEFAULT_COUNTRY_CODE, recipeDataTypeOptions } from "../../../../config/constants";
import { RecipeDataTypesOption } from "../../../../config/enums/IngredientType.enum";
import ProcessorCardHeader from "./ProcessorCardHeader";
import SemiSpecific from "./SemiSpecific";
import Specific from "./Specific";

interface ProcessorCardProps {
  processor: any;
  index: number;
  onChange: (processor: any) => void;
  onRemove: () => void;
  onSubmitSingle: () => void;
  variantId?: any;
  availableProcessesData?: any;
  availableProcesses?: any;
  defaultProcessName?: string;
  countries?: any;
  getSearchValue?: any;
  customResultTextView?: any;
  control?: any;
  setValue?: any;
  genericProcess?: any;
}

const ProcessorCard = ({
  processor,
  index,
  onChange,
  onRemove,
  onSubmitSingle,
  variantId,
  availableProcessesData,
  availableProcesses,
  defaultProcessName,
  getSearchValue,
  customResultTextView,
  control,
  setValue,
  countries,
  genericProcess
}: ProcessorCardProps) => {
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [nameMode, setNameMode] = useState<"select" | "text">("text");

  const update = (patch: Partial<any>) => onChange({ ...processor, ...patch });

  const dataType = useWatch({
    control,
    name: `processes.${index}.dataType`
  });

  const processName = useWatch({
    control,
    name: `processes.${index}.processName`
  });

  const dataTypeIsGeneric = dataType === RecipeDataTypesOption.GENERIC;
  const dataTypeIsSpecific = dataType === RecipeDataTypesOption.SPECIFIC;
  const dataTypeIsSemiSpecific = dataType === RecipeDataTypesOption.SEMI_SPECIFIC;
  const existingProcess = processor.saveStatue;
  const isNewProcess = processor.isNewProcess;

  const handleRemoveClick = () => {
    setIsRemoveModalOpen(true);
  };

  const handleConfirmRemove = () => {
    onRemove();
    setIsRemoveModalOpen(false);
  };

  const hasName = useMemo(() => {
    return !isEmpty(processName) && processName.trim().length > 0;
  }, [processName]);

  const enableCountry = useMemo(() => {
    return dataTypeIsSpecific || dataTypeIsSemiSpecific;
  }, [dataTypeIsSpecific, dataTypeIsSemiSpecific]);

  const hasAvailableProcesses = useMemo(() => {
    return (
      availableProcesses &&
      availableProcesses.length > 1 &&
      availableProcesses.some((p: any) => p.id !== 0)
    );
  }, [availableProcesses]);

  // Set initial name mode only when component mounts or when key conditions change
  useEffect(() => {
    // If it's an existing saved process, always use text mode
    if (existingProcess) {
      setNameMode("text");
      return;
    }

    // If in a variant and has available processes to select from, start with select mode
    if (variantId && hasAvailableProcesses && !processName && !isNewProcess) {
      setNameMode("select");
    } else {
      setNameMode("text");
    }
  }, [existingProcess, variantId, hasAvailableProcesses, processName, isNewProcess]);

  const handleProcessSelect = (data: any) => {
    const { result } = data;

    if (result?.id === 0) {
      update({
        processName: "",
        isNewProcess: true,
        isCollapsed: false,
        saveStatue: false
      });
      setNameMode("text");
    } else {
      const processData = result?.processData || result?.other?.processData;
      if (processData) {
        update({
          ...processData,
          country: processData.country?.toString() || getDefaultCountry || "47",
          isCollapsed: false,
          saveStatue: false,
          isNewProcess: false,
          genericProcessId: processData.genericProcessId?.toString() || null
        });

        setNameMode("text");
      }
    }

    if (getSearchValue) {
      getSearchValue(data);
    }
  };

  const handleNameCancel = () => {
    update({
      processName: "",
      isCollapsed: false,
      saveStatue: false,
      isNewProcess: false
    });

    setNameMode("select");
  };

  const getDefaultCountry = useMemo(() => {
    const denmarkValue = countries?.find((e: any) => e.alpha_code === DEFAULT_COUNTRY_CODE);
    return denmarkValue?.key?.toString() || "47";
  }, [countries]);

  return (
    <Card className="tw-border-border tw-shadow-sm tw-transition-all tw-duration-200 hover:tw-shadow-md tw-p-0 tw-gap-0 tw-w-full">
      <ProcessorCardHeader
        index={index}
        processName={processName}
        dataType={dataType}
        isCollapsed={processor.isCollapsed}
        onToggleCollapse={() => update({ isCollapsed: !processor.isCollapsed })}
        onRemove={handleRemoveClick}
      />

      <div
        className={`tw-transition-all tw-duration-300 tw-ease-in-out tw-space-y-0 ${
          processor.isCollapsed
            ? "tw-max-h-0 tw-opacity-0 tw-overflow-hidden"
            : "tw-max-h-[2000px] tw-opacity-100 tw-overflow-visible"
        }`}
      >
        <CardContent className="tw-px-3.5 tw-py-4 tw-space-y-5 tw-flex tw-flex-col">
          <div className="tw-text-sm tw-text-muted-foreground">
            <form
              onSubmit={e => {
                e.preventDefault();
                onSubmitSingle();
              }}
            >
              <div className="tw-space-y-4">
                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
                  <div className="tw-col-span-full md:tw-col-span-1">
                    {nameMode === "select" ? (
                      <SearchValue
                        searchDataValues={availableProcesses}
                        defaultValue={defaultProcessName}
                        title="Processing Name"
                        placeholder="Enter Process Name"
                        validationMessage="Please enter process name"
                        selectDetails={handleProcessSelect}
                        noResultEnable={true}
                        noResultData={{ title: "No results found" }}
                        disable={false}
                        customeResultTextEnable={true}
                        customResultTextView={customResultTextView}
                        checkOther={() => {}}
                        required
                      />
                    ) : (
                      <div className="tw-flex tw-gap-2 tw-items-start">
                        <div className="tw-flex-1">
                          <FormInput
                            control={control}
                            name={`processes.${index}.processName`}
                            label="Processing Name"
                            placeholder="Enter process name"
                            required
                            errorMessage="Please enter process Name"
                          />
                        </div>
                        {hasAvailableProcesses && (
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={handleNameCancel}
                            title="Cancel"
                            className="tw-shrink-0 tw-h-[36px] tw-w-[36px] tw-mt-[25px]"
                          >
                            <X className="tw-h-3.5 tw-w-3.5" />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="tw-col-span-full md:tw-col-span-1">
                    <FormSelect
                      control={control}
                      name={`processes.${index}.dataType`}
                      label="Data Type"
                      placeholder="Select a data type"
                      required
                      errorMessage="Please select a data type"
                      options={recipeDataTypeOptions}
                      disabled={existingProcess || !hasName}
                    />
                  </div>
                  {enableCountry && (
                    <div className="tw-col-span-full md:tw-col-span-1">
                      <FormSelect
                        control={control}
                        name={`processes.${index}.country`}
                        label="Processing Country"
                        placeholder="Select a processing country"
                        required={enableCountry}
                        errorMessage="Please select a processing country"
                        options={countries}
                        disabled={existingProcess || !hasName}
                      />
                    </div>
                  )}
                </div>

                {hasName ? (
                  <>
                    {dataTypeIsGeneric && (
                      <FormSelect
                        control={control}
                        name={`processes.${index}.genericProcessId`}
                        label="Generic Process Type"
                        placeholder="Select a generic process type"
                        required={dataTypeIsGeneric}
                        errorMessage="Please select a generic process type"
                        options={genericProcess}
                        disabled={existingProcess}
                      />
                    )}

                    {dataTypeIsSpecific && (
                      <Specific
                        control={control}
                        existingProcess={existingProcess}
                        index={index}
                        dataTypeIsSpecific={dataTypeIsSpecific}
                        countries={countries}
                      />
                    )}

                    {dataTypeIsSemiSpecific && (
                      <SemiSpecific
                        control={control}
                        setValue={setValue}
                        existingProcess={existingProcess}
                        index={index}
                      />
                    )}

                    <FormTextarea
                      control={control}
                      name={`processes.${index}.processNote`}
                      label="Notes"
                      placeholder="Add notes about this process"
                      errorMessage="Please enter processing Note"
                      disabled={existingProcess}
                    />
                    {!existingProcess && (
                      <div className="tw-flex tw-justify-end">
                        <Button size="sm" type="submit" variant="default">
                          Save
                        </Button>
                      </div>
                    )}
                  </>
                ) : null}
              </div>
            </form>
          </div>
        </CardContent>
      </div>
      <ConfirmModal
        isOpen={isRemoveModalOpen}
        onClose={() => setIsRemoveModalOpen(false)}
        onConfirm={handleConfirmRemove}
        title="Remove Process"
        message={`Are you sure you want to remove "${processName || `Process ${index + 1}`}"? `}
        confirmText="Remove"
        cancelText="Cancel"
        confirmVariant="destructive"
      />
    </Card>
  );
};

export default ProcessorCard;
